import React, { useState, useEffect, useCallback } from 'react';
import SMSLayout from '../components/SMSLayout';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  BarChart3, RefreshCw, Calendar, Download, TrendingUp, TrendingDown,
  Send, CheckCircle, XCircle, Users, MessageSquare, Clock, Loader2,
  FileDown, Mail
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const SMSStatistics = () => {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [stats, setStats] = useState({
    total_campaigns: 0,
    total_sms: 0,
    delivered: 0,
    failed: 0,
    delivery_rate: 0
  });
  const [dailyData, setDailyData] = useState([]);
  const [campaignTypeData, setCampaignTypeData] = useState([]);
  const [topCampaigns, setTopCampaigns] = useState([]);

  const fetchStatistics = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch campaigns and calculate statistics
      const response = await axios.get(`${API_URL}/sms-mailing/campaigns`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 100 }
      });
      
      const campaigns = response.data.campaigns || [];
      
      // Calculate date range
      const now = new Date();
      let startDate;
      switch (period) {
        case '7d':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30d':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '90d':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = new Date(0);
      }
      
      // Filter campaigns by period
      const filteredCampaigns = campaigns.filter(c => 
        new Date(c.created_at) >= startDate
      );
      
      // Calculate stats
      const totalSms = filteredCampaigns.reduce((sum, c) => sum + (c.sent_count || c.recipients_count || 0), 0);
      const delivered = filteredCampaigns.reduce((sum, c) => sum + (c.delivered_count || c.sent_count || 0), 0);
      const failed = filteredCampaigns.reduce((sum, c) => sum + (c.failed_count || 0), 0);
      
      setStats({
        total_campaigns: filteredCampaigns.length,
        total_sms: totalSms,
        delivered: delivered,
        failed: failed,
        delivery_rate: totalSms > 0 ? Math.round((delivered / totalSms) * 100) : 0
      });
      
      // Generate daily data
      const dailyMap = {};
      filteredCampaigns.forEach(c => {
        const date = new Date(c.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
        if (!dailyMap[date]) {
          dailyMap[date] = { date, campaigns: 0, sms: 0, delivered: 0 };
        }
        dailyMap[date].campaigns++;
        dailyMap[date].sms += c.sent_count || c.recipients_count || 0;
        dailyMap[date].delivered += c.delivered_count || c.sent_count || 0;
      });
      setDailyData(Object.values(dailyMap).slice(-14)); // Last 14 days
      
      // Campaign type distribution
      const typeCount = { marketing: 0, transactional: 0 };
      filteredCampaigns.forEach(c => {
        typeCount[c.type || 'marketing']++;
      });
      setCampaignTypeData([
        { name: 'Marketing', value: typeCount.marketing, color: '#6366f1' },
        { name: 'Transactionnel', value: typeCount.transactional, color: '#10b981' }
      ]);
      
      // Top campaigns by volume
      const sorted = [...filteredCampaigns].sort((a, b) => 
        (b.sent_count || b.recipients_count || 0) - (a.sent_count || a.recipients_count || 0)
      );
      setTopCampaigns(sorted.slice(0, 5));
      
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, [token, period]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  // Export functions
  const handleExportPDF = async () => {
    toast.info('Export PDF en cours de préparation...');
    try {
      const response = await axios.get(`${API_URL}/sms-mailing/statistics/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { period },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `statistiques_sms_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export PDF terminé');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Erreur lors de l\'export PDF');
    }
  };

  const handleSendReport = async () => {
    toast.info('Envoi du rapport par email...');
    try {
      await axios.post(`${API_URL}/sms-mailing/statistics/send-report`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { period }
      });
      toast.success('Rapport envoyé par email');
    } catch (error) {
      console.error('Send report error:', error);
      toast.error('Erreur lors de l\'envoi du rapport');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <SMSLayout>
      <div className="space-y-6" data-testid="sms-statistics-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Statistiques SMS
            </h1>
            <p className={`mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Analysez les performances de vos campagnes SMS
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className={`w-[180px] ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}>
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">7 derniers jours</SelectItem>
                <SelectItem value="30d">30 derniers jours</SelectItem>
                <SelectItem value="90d">90 derniers jours</SelectItem>
                <SelectItem value="all">Tout</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              onClick={fetchStatistics}
              disabled={loading}
              className={isDarkMode ? 'border-[hsl(220,14%,25%)] hover:bg-[hsl(220,14%,18%)]' : ''}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            
            <Button
              variant="outline"
              onClick={handleExportPDF}
              className={isDarkMode ? 'border-[hsl(220,14%,25%)] hover:bg-[hsl(220,14%,18%)]' : ''}
            >
              <FileDown className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
            
            <Button
              onClick={handleSendReport}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Mail className="h-4 w-4 mr-2" />
              Envoyer le rapport
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-12 w-12 animate-spin text-indigo-500" />
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
                      <BarChart3 className={`h-5 w-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {stats.total_campaigns}
                      </p>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Campagnes
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                      <Send className={`h-5 w-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {stats.total_sms.toLocaleString()}
                      </p>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        SMS envoyés
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                      <CheckCircle className={`h-5 w-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {stats.delivered.toLocaleString()}
                      </p>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Délivrés
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-red-500/20' : 'bg-red-100'}`}>
                      <XCircle className={`h-5 w-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {stats.failed}
                      </p>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Échecs
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${stats.delivery_rate >= 90 ? (isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100') : (isDarkMode ? 'bg-amber-500/20' : 'bg-amber-100')}`}>
                      {stats.delivery_rate >= 90 ? (
                        <TrendingUp className={`h-5 w-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                      ) : (
                        <TrendingDown className={`h-5 w-5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                      )}
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {stats.delivery_rate}%
                      </p>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Taux délivraison
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* SMS Volume Chart */}
              <Card className={`lg:col-span-2 ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
                <CardHeader>
                  <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>
                    {"Volume d'envoi SMS"}
                  </CardTitle>
                  <CardDescription className={isDarkMode ? 'text-slate-400' : ''}>
                    Évolution sur la période sélectionnée
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {dailyData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={dailyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#334155' : '#e2e8f0'} />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fill: isDarkMode ? '#94a3b8' : '#64748b', fontSize: 12 }}
                        />
                        <YAxis 
                          tick={{ fill: isDarkMode ? '#94a3b8' : '#64748b', fontSize: 12 }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: isDarkMode ? '#1e293b' : '#fff',
                            border: isDarkMode ? '1px solid #334155' : '1px solid #e2e8f0',
                            borderRadius: '8px'
                          }}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="sms" 
                          name="SMS envoyés"
                          stroke="#6366f1" 
                          strokeWidth={2}
                          dot={{ fill: '#6366f1' }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="delivered" 
                          name="Délivrés"
                          stroke="#10b981" 
                          strokeWidth={2}
                          dot={{ fill: '#10b981' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className={`text-center py-12 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Aucune donnée pour cette période</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Campaign Type Distribution */}
              <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
                <CardHeader>
                  <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>
                    Types de campagnes
                  </CardTitle>
                  <CardDescription className={isDarkMode ? 'text-slate-400' : ''}>
                    Répartition par type
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {campaignTypeData.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={campaignTypeData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {campaignTypeData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className={`text-center py-12 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Aucune donnée</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Top Campaigns */}
            <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
              <CardHeader>
                <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>
                  Top 5 des campagnes
                </CardTitle>
                <CardDescription className={isDarkMode ? 'text-slate-400' : ''}>
                  {"Campagnes avec le plus grand volume d'envoi"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {topCampaigns.length > 0 ? (
                  <div className="space-y-3">
                    {topCampaigns.map((campaign, index) => (
                      <div
                        key={campaign.id}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className={`w-8 h-8 flex items-center justify-center ${
                            index === 0 ? 'bg-amber-100 text-amber-700 border-amber-300' :
                            index === 1 ? 'bg-slate-100 text-slate-700 border-slate-300' :
                            index === 2 ? 'bg-orange-100 text-orange-700 border-orange-300' :
                            ''
                          }`}>
                            {index + 1}
                          </Badge>
                          <div>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              {campaign.name || 'Sans nom'}
                            </p>
                            <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                              {formatDate(campaign.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <Badge variant="outline" className={campaign.type === 'marketing' ? 'border-indigo-400 text-indigo-600' : 'border-emerald-400 text-emerald-600'}>
                            {campaign.type === 'marketing' ? 'Marketing' : 'Transaction'}
                          </Badge>
                          <div className="text-right">
                            <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              {(campaign.sent_count || campaign.recipients_count || 0).toLocaleString()}
                            </p>
                            <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                              SMS
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={`text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Aucune campagne pour cette période</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </SMSLayout>
  );
};

export default SMSStatistics;
